import { useEffect, useState } from "react";
import { Flame, MailOpen, MousePointerClick, Reply, MailCheck, ChevronRight } from "lucide-react";
import api from "@/lib/api";

const ICON = {
  delivered: MailCheck, opened: MailOpen, clicked: MousePointerClick, replied: Reply,
};

const ICON_COLOR = {
  delivered: "text-gray-500", opened: "text-[#2563EB]",
  clicked: "text-[#D97706]", replied: "text-[#059669]",
};

function timeAgo(iso) {
  const then = new Date(iso).getTime();
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function HotLeads({ country, onSelectCompany }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/hot_leads", { params: { country, days: 7 } })
      .then((r) => setData(r.data));
  }, [country]);

  if (!data) return null;

  const { leads = [] } = data;

  return (
    <div className="section-card p-5" data-testid="hot-leads-widget">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Flame size={16} className="text-[#D97706]" />
          <div className="label-eyebrow">Hot Leads</div>
        </div>
        <span className="text-xs text-gray-400">last 7 days</span>
      </div>
      <div className="font-display text-lg font-semibold mt-1 mb-4">Recently engaged prospects</div>

      {leads.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-10">
          No engagement events yet. Send some prospecting emails to surface hot leads here.
        </div>
      ) : (
        <ol className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {leads.map((l, i) => (
            <li
              key={l.company_id}
              data-testid={`hot-lead-${l.company_id}`}
              onClick={() => onSelectCompany?.(l.company_id)}
              className="flex items-center gap-3 p-3 border border-gray-200 hover:border-[#D97706] hover:bg-amber-50/30 cursor-pointer transition-colors"
            >
              <div className="text-[11px] font-mono text-gray-400 w-5">#{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{l.company_name}</div>
                <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                  <span className="capitalize">{(l.sector || "").replace(/_/g, " ")}</span>
                  {l.emirate && <span>·</span>}
                  {l.emirate && <span>{l.emirate}</span>}
                  <span>·</span>
                  <span>{timeAgo(l.last_activity)}</span>
                </div>
                {/* breakdown chips */}
                <div className="flex items-center gap-2 mt-1.5">
                  {Object.entries(l.breakdown).map(([k, v]) => {
                    if (!v) return null;
                    const Ico = ICON[k];
                    return (
                      <span key={k} className={`inline-flex items-center gap-1 text-[11px] ${ICON_COLOR[k]}`}>
                        <Ico size={11} /> {v}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-col items-end">
                <div className="font-display text-lg font-bold text-[#D97706] leading-none">{l.score}</div>
                <div className="label-eyebrow mt-1">score</div>
              </div>
              <ChevronRight size={14} className="text-gray-300" />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
